import { ErrorBoundary } from "@sentry/react";
import { isAxiosError } from "axios";
import { Suspense, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod/v4";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useForm } from "../common/form/useForm";
import { IconSVG } from "../common/Icon";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { Button } from "../components/Button";
import { TextDash } from "../components/TextDash";
import { useBranding } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { AuthPage } from "../layout/AuthLayout";
import {
	useFacebookUnprotectedAction,
	useGoogleProfileActions,
} from "../settings/useIntegrations";
import { InvitationCard } from "./InvitationCard";
import { useAuth, useInvitation } from "./useAuth";

const Schema = z.object({
	email: z
		.string({
			error: "Email field is required.",
		})
		.email("Email is not valid."),
	password: z
		.string({
			error: "Password field is required.",
		})
		.min(8, "Password should be minimum 8 chars"),
});

export const PageSignIn = () => {
	const { login, verify } = useAuth();
	const branding = useBranding();
	const { connect: connectFacebook } = useFacebookUnprotectedAction();
	const { connect: connectGoogle } = useGoogleProfileActions();
	const params = new URLSearchParams(window.location.search);
	const message = params.get("message");
	const emit = useAlertEmit();
	const [searchParams] = useSearchParams();
	const inviteToken = searchParams.get("inviteToken") ?? undefined;
	const { isError: isInvitationError, isLoading: isInvitationLoading } =
		useInvitation(inviteToken);

	const [verificationError, setVerificationError] = useState(false);

	useEffect(() => {
		const verifyCode = async () => {
			const params = new URLSearchParams(window.location.search);
			const code = params.get("code");
			const userId = params.get("id");
			if (code && userId) {
				params.delete("code");
				params.delete("id");
				window.history.replaceState(
					{},
					"",
					`${window.location.pathname}?${params.toString()}`,
				);
				await verify.mutateAsync({ code, id: Number.parseInt(userId) });
			}
		};
		verifyCode();
	}, [verify]);

	useEffect(() => {
		if (message) {
			emit({ type: AlertType.ERROR, content: message });
		}
	}, [message, emit]);

	const form = useForm({
		validationSchema: toFormikValidationSchema(Schema),
		initialValues: {
			email: "",
			password: "",
		},
		onSubmit: (values) =>
			login
				.mutateAsync({
					...values,
					inviteToken,
				})
				.catch((e) => {
					if (e?.status === 424) {
						emit({
							type: AlertType.ERROR,
							content:
								e?.response?.data?.message ??
								"Verify your email before logging in",
						});
						setVerificationError(true);
					}
					if (e?.status === 400 && isAxiosError(e)) {
						emit({
							type: AlertType.ERROR,
							content:
								e?.response?.data.message ?? "Incorrect username or password",
						});
					}
				}),
	});
	return (
		<AuthPage title="Sign in">
			{inviteToken && (
				<ErrorBoundary
					fallback={({ error }) => <InvitationCard.Error error={error} />}
				>
					<Suspense fallback={<InvitationCard.Suspense />}>
						<InvitationCard
							token={inviteToken}
							cta="Please sign in below to join"
						/>
					</Suspense>
				</ErrorBoundary>
			)}
			<Form className="auth-form" onSubmit={form.handleSubmit}>
				<h1 className="my-4">{`Sign in to ${branding.appName}`}</h1>

				<div className="d-flex flex-column gap-3">
					<Button
						icon={IconSVG.Facebook}
						iconSize={24}
						variant="social"
						full
						onClick={() => connectFacebook(inviteToken)}
					>
						Sign in with Facebook
					</Button>

					<Button
						icon={IconSVG.Google}
						iconSize={24}
						variant="social"
						full
						onClick={() => connectGoogle(inviteToken)}
					>
						Sign in with Google
					</Button>
				</div>

				<TextDash variant="gray" className="my-5">
					or sign in with email
				</TextDash>

				<ExtraForm.Group label="Email">
					<ExtraForm.Control
						placeholder="john.doe@example.com"
						type="email"
						{...form.getFieldProps("email")}
						{...form.getFieldState("email")}
					/>
				</ExtraForm.Group>

				<ExtraForm.Group label="Password">
					<ExtraForm.Control
						placeholder="*********"
						type="password"
						{...form.getFieldProps("password")}
						{...form.getFieldState("password")}
					/>
				</ExtraForm.Group>

				<Button
					full
					className="my-3"
					loading={form.isSubmitting}
					disabled={isInvitationError || isInvitationLoading}
					type="submit"
				>
					Sign in
				</Button>
				<div className="auth-form-actions">
					{verificationError && (
						<p>
							Having trouble with verification?{" "}
							<Link to={LinkGen.VerificationResend()}>
								Resend verification email
							</Link>
						</p>
					)}
					Don’t have an account?{" "}
					<Link to={LinkGen.Register(inviteToken)}>Sign Up</Link>
					<br />
					<Link to={LinkGen.PasswordResetRequest()}>Forgotten password</Link>
				</div>
				<footer>
					{branding.footerText ??
						`© ${new Date().getFullYear()} ${branding.appName}. All rights reserved`}
				</footer>
			</Form>
		</AuthPage>
	);
};
